import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RenderedOutputStatus } from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ListSituConnectSummariesRequest, ListSituConnectSummariesResponse, RecipeStatus, SituAnalyticsWebApiClient, SituConnectSummary } from 'situanalytics-webapi-client';
import { Company, IdentityWebApiClient, ListUsersRequest, ListUsersResponse } from 'identity-webapi-client';
import { SelectableItem } from 'src/app/shared/models/selectable-item';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'situ-connect-config-list',
  templateUrl: './situ-connect-config-list.component.html',
  styleUrls: ['./situ-connect-config-list.component.scss']
})
export class SituConnectConfigListComponent extends SituBaseComponent implements OnChanges {
  @Input() viewSituConnectId: string | undefined;

  @Output() selectedSituConnect: EventEmitter<SituConnectSummary> = new EventEmitter();

  
  items: SituConnectSummary[] = [];

  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private identityWebApiClient: IdentityWebApiClient) {
      super(authService, stateService)
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadRecipeEventSummaries();
    if (this.isSituUser || this.isAdmin) {
      this.loadUsers();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  protected onSelectedCompanyChange(): void {
    this.loadRecipeEventSummaries();
  }

  connectTypeFilter: string | undefined;
  setConnectTypeFilter(connectType: string) {
    if (this.connectTypeFilter === connectType) {
      this.connectTypeFilter = undefined;
    } else {
      this.connectTypeFilter = connectType;
    }
  }

  searchCustomer: boolean = false;
  searchTerm: string | undefined;

  searchSourceUser: boolean = false;
  searchUsersField = new FormControl();
  users: SelectableItem[] | undefined;
  filteredUsers: SelectableItem[] | undefined;
  doneSelectingTemplates: boolean = false;
  selectedUser: SelectableItem | undefined;
  selectUser(user: SelectableItem) {
    if (user?.item && user?.id) {
      this.selectedUser = user;
      this.searchSourceUser = false;
      this.loadRecipeEventSummaries();
    }
  }
  loadUsers() {
    let request = new ListUsersRequest();
    request.pageNumber = 1;
    request.pageSize = 300;
    request.enabledOnly = true;

    if (!this.isSituUser) {
        let companies = this.authService.listCompanies();
        let userPromises = companies.map((company: Company) => {
            let companyRequest = { ...request };
            companyRequest.companyId = company.id;
            return this.identityWebApiClient.listUsers(request);
        });

        return Promise.all(userPromises)
            .then((responses: ListUsersResponse[]) => {
                let allUsers: SelectableItem[] = [];
                responses.forEach((response) => {
                    if (response.success && response.items) {
                        let users = response.items.map(x => new SelectableItem(x.email!, false, x.id));
                        allUsers = allUsers.concat(users);
                    } else {
                        this.showMessage("There was an issue retrieving users for a company!");
                    }
                });
                this.users = allUsers;
                this.filteredUsers = [...this.users];
            })
            .catch(() => {
                this.showMessage("There was an error loading users.");
            })
            .finally();
    } else {
        return this.identityWebApiClient.listUsers(request)
            .then((response: ListUsersResponse) => {
                if (response.success && response.items) {
                    this.users = response.items.map(x => new SelectableItem(x.email!, false, x.id));
                    this.filteredUsers = [...this.users];
                } else {
                    this.showMessage("There was an issue retrieving users!");
                }
            })
            .catch(() => {
                this.showMessage("There was an error loading users.");
            })
            .finally();
    }
  }
  doUserFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredUsers = this.users?.filter(e => { return e.item?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredUsers = this.users;
    }
  }

  getIconType(connectType: string): string {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
      case 'situlinkpanoramas':
      case 'hdsitulinkpanoramas':
        return 'panorama'; // the icon for panorama
      case 'facade':
      case 'hdfacade':
        return 'photo'; // the icon for photo
      default:
        return 'panorama'; // fallback icon
    }
  }
  getConnectTypeTooltip(connectType: string): string {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
        return 'situVIEW';
      case 'situlinkpanoramas':
        return 'situVIEW HD';
      case 'hdsitulinkpanoramas':
        return 'situVIEW HD+';
      case 'facade':
        return 'situFACADE';
      case 'hdfacade':
        return 'situFACADE HD';
      default:
        return 'Unknown Type';
    }
  }
  getIconColor(connectType: string): string {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
        return 'rgba(106, 76, 147, 1)'; // purple
      case 'situlinkpanoramas':
        return 'rgba(25, 130, 196, 1)'; // blue
      case 'hdsitulinkpanoramas':
        return 'rgba(20, 193, 245, 1)'; // light blue
      case 'facade':
      case 'hdfacade':
        return 'rgba(255, 89, 94, 1)'; // red
      default:
        return 'rgba(0, 0, 0, 1)'; // default black
    }
  }
  isViewable(connectType: string): boolean {
    switch (connectType) {
      case 'draftsitulinkpanoramas':
      case 'situlinkpanoramas':
      case 'hdsitulinkpanoramas':
      case 'facade':
      case 'hdfacade':
        return true;
      default:
        return false;
    }
  }

  loadRecipeEventSummaries() {
    this.loading = true;
    let request = new ListSituConnectSummariesRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = 1;
    request.pageSize = 10;
    request.recipeStatus = RecipeStatus.Complete;
    if (this.searchTerm) request.searchTerm = this.searchTerm;
    if (this.connectTypeFilter) request.connectType = this.connectTypeFilter;
    if (this.selectedUser) {
      request.sourceName = this.selectedUser.item;
    } else if (!(this.isAdmin || this.isSituUser)) {
      request.sourceName = this.authService.getUserName();
    }

    this.situAnalyticsClient.listSituConnectSummaries(request)
      .then((response: ListSituConnectSummariesResponse) => {
        if (response.success && response?.items) {
          this.items = response.items;
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  select(situConnect: SituConnectSummary) {
    this.selectedSituConnect.emit(situConnect);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export class HomeConfigFilter {
  constructor(public type: string, public value: string, public listType: string) {
  }
}